if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      const oldCartPromise = ProductForm.getCart();
      let added = false;
      try {
        const response = await fetch(theme.routes.cartAdd, {
          ...fetchRequestOpts,
          signal: AbortSignal.timeout(15000)
        });
        if (!response.ok) {
          const failure = await response.json().catch(() => ({}));
          throw new Error(typeof failure.description === 'string'
            ? failure.description : theme.strings.cartError);
        }
        added = true;
        const data = await response.json();

        if (theme.settings.afterAtc === 'page') {
          window.location.assign(theme.routes.cart);
        } else {
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (this.cartDrawer) {
            await this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else {
            ProductForm.updateCartIcon(data);
            if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems?.dataset.empty === 'true') window.location.reload();
              else if (cartItems) await cartItems.refresh();
            }
          }
          if (quickAddDrawer) quickAddDrawer.addedToCart();
        }

        Promise.all([oldCartPromise, ProductForm.getCart()]).then(([oldCart, cart]) => {
          if (!cart || !oldCart) return;
          const previous = oldCart?.items.find((item) => item.variant_id === data.variant_id);
          const current = cart.items.find((item) => item.variant_id === data.variant_id);
          this.dispatchEvent(new CustomEvent(previous ? 'on:line-item:change' : 'on:cart:add', {
            bubbles: true,
            detail: {
              cart,
              variantId: data.variant_id,
              ...(previous ? { oldQuantity: previous.quantity, newQuantity: current?.quantity || 0 } : {})
            }
          }));
        }).catch(() => {});
      } catch (error) {
        if (added || error.name === 'TimeoutError' || error.name === 'AbortError' || error instanceof TypeError) {
          window.location.assign(theme.routes.cart);
        } else {
          this.setErrorMsgState(error.message || theme.strings.cartError);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: error.message || theme.strings.cartError }
          }));
        }
      } finally {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        if (added) {
          this.submitBtn.classList.add('is-success');
          setTimeout(() => this.submitBtn.classList.remove('is-success'), 1000);
        }
      }
    }

    static async getCart() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`, { signal: AbortSignal.timeout(5000) });
        return response.ok ? await response.json() : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && typeof response.sections?.['cart-icon-bubble'] === 'string') {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
